import React from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { DocumentStep, DocumentStepData } from '@REDUCERS';

import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';
import { useGetAllDocuments } from '@SERVICES';

import Grid from '@COMPONENTS/SHARED/Grid';
import SquareButton from '@COMPONENTS/COMMON/buttons/SquareButton';
import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import EmptyStateHint from '@COMPONENTS/SHARED/EmptyStateHint';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import styles from './ChooseDocument.module.scss';

const cx: CX = classnames.bind(styles);

function ChooseDocumentPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps(() => true);

    const {
        data: documentsData = [], isLoading, isError, error,
    } = useGetAllDocuments();

    const navigate = useNavigate();

    const documentStep: DocumentStep = documentGenerationStepsServices.getStepData(GenerationSteps.DOCUMENT);
    const hasSelectedDocument = documentStep.isComplete;

    function getHighlighted(id: number) {
        const step: DocumentStep = documentGenerationStepsServices.getStepData(GenerationSteps.DOCUMENT);

        return step.data?.id === id;
    }

    const handleNext = () => {
        if (hasSelectedDocument) {
            navigate(ROUTES.type.path);
        }
    };

    return (
        <div className={cx('page')}>
            <h1>Choose a Document</h1>
            <DataLoader
                isLoading={isLoading}
                isError={isError}
                error={error}
            >
                {documentsData.length === 0 ? (
                    <EmptyStateHint
                        type="documents"
                        title="No Documents Available"
                        description="It looks like there are no documents available for you to generate. This could be because you don't have access to any documents yet, or no documents have been created. Please contact your document manager to request access to documents or ask them to create new document templates."
                    />
                ) : (
                    <Grid>
                        {documentsData.map((doc) => {
                            const key = doc.id;
                            const highlighted = getHighlighted(doc.id);

                            return (
                                <SquareButton
                                    key={key}
                                    highlighted={highlighted}
                                    title={doc.name}
                                    onClick={() => {
                                        const data: DocumentStepData = {
                                            id: doc.id,
                                            name: doc.name,
                                        };

                                        documentGenerationStepsActions.apply({
                                            id: GenerationSteps.DOCUMENT,
                                            isComplete: true,
                                            data,
                                        });

                                        navigate(ROUTES.type.path);
                                    }}
                                />
                            );
                        })}
                    </Grid>
                )}
            </DataLoader>

            {hasSelectedDocument && (
                <StepNavigation
                    onNext={handleNext}
                    nextText="Continue to Type"
                    sticky
                />
            )}
        </div>
    );
}

export default ChooseDocumentPage;
